import React, { useEffect, useState } from 'react';
import { Alert, Card, List, Spin, Tag, Typography, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { getCentralLoginRedirect } from '../config';
import { getApiErrorMessage } from '../utils/apiError';
import { User } from '../types';

const { Title, Text, Paragraph } = Typography;

interface DevUser {
  username: string;
  full_name: string;
  role: string;
  department?: string | null;
}

/**
 * Боевой вход идёт только через auth.pikinlab.ru, формы логина здесь нет.
 *
 * Исключение — локальная разработка: если backend отдаёт список сотрудников
 * по /auth/dev-users, показываем выбор без пароля. В проде этот эндпоинт
 * отвечает 404, и страница ведёт себя как раньше — редиректит на SSO.
 */
const Login: React.FC = () => {
  const navigate = useNavigate();
  const { loginWithSso } = useAuth();
  const [devUsers, setDevUsers] = useState<DevUser[] | null>(null);
  const [checking, setChecking] = useState(true);
  const [signingIn, setSigningIn] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    api.get<DevUser[]>('/auth/dev-users')
      .then(({ data }) => {
        if (cancelled) return;
        setDevUsers(data);
        setChecking(false);
      })
      .catch(() => {
        if (cancelled) return;
        // Вход для разработки недоступен — обычное поведение.
        if (typeof window !== 'undefined') {
          window.location.replace(getCentralLoginRedirect(`${window.location.origin}/`));
        }
        setChecking(false);
      });

    return () => { cancelled = true; };
  }, []);

  const signIn = async (username: string) => {
    setSigningIn(username);
    try {
      const { data } = await api.get<{ access_token: string; user: User }>(
        '/auth/dev-session',
        { params: { username } },
      );
      loginWithSso(data.access_token, data.user);
      navigate('/', { replace: true });
    } catch (error) {
      message.error(getApiErrorMessage(error, 'не удалось войти'));
      setSigningIn(null);
    }
  };

  if (checking) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 120 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!devUsers) return null;

  return (
    <div style={{ maxWidth: 560, margin: '48px auto', padding: '0 16px' }}>
      <Title level={3}>FEBEST · Обучение</Title>
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message="Локальный режим разработки"
        description="Вход без пароля работает только на этой машине: он требует DEBUG, отдельного флага и отключённого SSO. На рабочем сервере эта страница отправляет на auth.pikinlab.ru."
      />
      <Paragraph type="secondary">
        Выберите, от чьего имени открыть систему. Администратор видит админ-панель,
        сотрудник — только назначенные ему тесты.
      </Paragraph>
      <Card size="small">
        <List
          dataSource={devUsers}
          renderItem={(item) => (
            <List.Item
              onClick={() => !signingIn && signIn(item.username)}
              style={{ cursor: signingIn ? 'wait' : 'pointer' }}
              actions={[signingIn === item.username ? <Spin size="small" /> : null]}
            >
              <List.Item.Meta
                title={
                  <span>
                    {item.full_name || item.username}{' '}
                    {item.role === 'admin' && <Tag color="purple">админ</Tag>}
                  </span>
                }
                description={<Text type="secondary">{item.department || 'без отдела'}</Text>}
              />
            </List.Item>
          )}
        />
      </Card>
    </div>
  );
};

export default Login;
